import React, { useContext, useEffect, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import fetchAll from '../../../utils/fetchAll';
import useFetchWithCallbacks from '../../../hooks/useFetchWithCallbacks';
import useTranslations from '../../../hooks/useTranslations';
import LookupSettingsPanelView from '../view/LookupSettingsPanelView';
import { UPDATE_LOOKUP_FIELD } from '../../../store/modules/state/formBuilder';
import { FormBuilderEditContext } from '../FormBuilderEdit';
import useGlobalVariables from '../../../hooks/useGlobalVariables';

const allExtraOptions = [
  {
    title: 'System fields',
    uiType: 'section_break'
  },
  {
    title: 'forms.field.lookup.submitterId',
    internalId: 'submitterId',
    uiType: 'text'
  },
  {
    title: `forms.field.lookup.'metadata'->'createdDate'`,
    internalId: "'metadata'->'createdDate'",
    uiType: 'date'
  },
  {
    title: `forms.field.lookup.'metadata'->'updatedDate'`,
    internalId: "'metadata'->'updatedDate'",
    uiType: 'date'
  },
  {
    title: `forms.field.lookup.'statusData'->'status'`,
    internalId: "'statusData'->'status'",
    uiType: 'status'
  },
  {
    title: <hr />,
    uiType: 'section_break'
  }
];

const LookupSettingsPanelEngine = props => {
  const t = useTranslations();
  const { setIsLoading } = useGlobalVariables();
  const {
    fields: preExistingEndUserFields,
    adminFields: preExistingAdminFields,
    rules,
    setRules,
    sharingType
  } = useContext(FormBuilderEditContext);
  let extraOptions;
  if (sharingType === 'anonymous') {
    extraOptions = allExtraOptions.filter(
      one => one.internalId !== 'submitterId'
    );
  } else {
    extraOptions = allExtraOptions;
  }
  const preExistingFields = [
    ...preExistingEndUserFields,
    ...preExistingAdminFields
  ];
  const fetchAPI = useFetchWithCallbacks();
  const [selectedSubFields, setSelectedSubFields] = useState([]);
  const [selectedUserGroups, setSelectedUserGroups] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [subFieldOptions, setSubFieldOptions] = useState([]);
  const [customStatuses, setCustomStatuses] = useState([]);
  const [unfilteredSubFieldOptions, setUnfilteredSubFieldOptions] = useState(
    []
  );
  const [forms, setForms] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [step2Disabled, setStep2Disabled] = useState(true);
  const [step3Disabled, setStep3Disabled] = useState(
    props.entityType === 'user' ? false : true
  );
  const { autosaveField, setShowLookupDialog } = props;

  const clickedSave = () => {
    let matchingFields = [];
    let subFieldsToAdd = [];
    let lookupInfoSection;
    setIsLoading(true);
    matchingFields = selectedSubFields.map(one => {
      let matches = fields.filter(field => field.internalId === one);
      let newField;
      if (matches && matches.length) {
        newField = cloneDeep(matches[0]);
        if (!newField.uiType) {
          if (newField.type === 'date') {
            newField.uiType = 'date';
            newField.type = 'number';
          } else {
            newField.uiType = 'text';
          }
        }
        return newField;
      }
      if (!matches.length && one.indexOf('metadata') !== -1) {
        return {
          internalId: one,
          options: [],
          type: 'number',
          uiType: 'date',
          title: t(`forms.field.lookup.${one}`)
        };
      }
      if (!matches.length && one.indexOf('statusData') !== -1) {
        return {
          internalId: one,
          options: customStatuses || [],
          type: 'text',
          uiType: 'status',
          title: t(`forms.field.lookup.${one}`)
        };
      }
      if (!matches.length && one === 'submitterId') {
        return {
          internalId: 'submitterId',
          options: [],
          type: 'text',
          uiType: 'text',
          title: t(`forms.field.lookup.${one}`)
        };
      }
      return null;
    });

    let preExistingSubFields = preExistingFields.filter(one => {
      return (
        one.lookupInfo &&
        one.lookupInfo.subFieldInfo &&
        one.lookupInfo.subFieldInfo.parentFieldId === props.internalId
      );
    });

    let matchingFieldIds = matchingFields.map(one => one.internalId);

    let unusedPreExistingSubFields = preExistingSubFields.filter(one => {
      if (
        matchingFieldIds.indexOf(
          one.lookupInfo.subFieldInfo.externalFormFieldId
        ) === -1
      ) {
        return true;
      }
      return false;
    });

    matchingFields.forEach(one => {
      let internalId, matchingPreExistingSubFields;
      internalId = one.internalId;
      if (!one) {
        return;
      }
      one.lookupInfo = {
        subFields: [],
        subFieldInfo: {
          parentFieldId: props.internalId,
          externalFormFieldId: internalId
        }
      };
      matchingPreExistingSubFields = preExistingSubFields.filter(
        oneSubField => {
          return (
            oneSubField.lookupInfo.subFieldInfo.externalFormFieldId ===
            internalId
          );
        }
      );
      if (
        !matchingPreExistingSubFields ||
        !matchingPreExistingSubFields.length
      ) {
        delete one.systemField;
        subFieldsToAdd.push(one);
      }
    });

    if (unusedPreExistingSubFields.length) {
      unusedPreExistingSubFields.forEach(one => {
        deleteFieldRules(one.internalId);
      });
    }
    lookupInfoSection = {
      entityType: props.entityType,
      subFields: selectedSubFields.map(one => {
        return {
          internalId: one
        };
      })
    };
    if (selectedForm) {
      lookupInfoSection.entityId = selectedForm;
    }
    if (selectedField) {
      lookupInfoSection.keyField = selectedField;
    }
    let fieldObjectToSave = {
      lookupInfo: lookupInfoSection
    };
    let selectedUserGroupsThatActuallyExist = [];
    let userGroupIds = [];
    if (props.entityType === 'user') {
      userGroupIds = userGroups.map(one => one.id);
      if (selectedUserGroups.length && userGroups.length) {
        selectedUserGroupsThatActuallyExist = selectedUserGroups.filter(one =>
          userGroupIds.includes(one)
        );
      }
      fieldObjectToSave.options = selectedUserGroupsThatActuallyExist;
    }
    autosaveField(UPDATE_LOOKUP_FIELD, {
      field: fieldObjectToSave,
      isAdminOnly: props.isAdminOnly,
      subFieldsToAdd: subFieldsToAdd,
      subFieldsToDelete: unusedPreExistingSubFields
    });

    setTimeout(() => {
      setShowLookupDialog(false);
      setIsLoading(false);
    }, 1000);
  };

  const excludeInvalidRules = (conditions, ruleId) => {
    let conditionsObj = JSON.parse(conditions);
    for (let i = conditionsObj.length - 1; i >= 0; i--) {
      if (conditionsObj[i][0].field.includes(ruleId)) {
        conditionsObj.splice(i, 1);
      }
    }

    return JSON.stringify(conditionsObj);
  };

  const excludeInvalidDescriptionRules = (conditions, ruleId) => {
    let conditionsObj = JSON.parse(conditions);
    for (let i = conditionsObj.length - 1; i >= 0; i--) {
      const singleDescriptionConditions = conditionsObj[i].conditions;
      for (let j = singleDescriptionConditions.length - 1; j >= 0; j--) {
        if (singleDescriptionConditions[j].input === ruleId) {
          singleDescriptionConditions.splice(j, 1);
        }
      }
    }

    return JSON.stringify(conditionsObj);
  };

  const deleteFieldRules = React.useCallback(
    fieldIdToDelete => {
      let rules2DeleteFromState = [];

      // delete rules on server
      rules.forEach(r => {
        if (r.fieldId === fieldIdToDelete) {
          rules2DeleteFromState.push(r.id);
          fetchAPI(`/forms/v1/rules/${r.id}`, { method: 'DELETE' });
        }
      });

      // delete rules in state
      rules.forEach(rule => {
        if (rule.condition.includes(fieldIdToDelete)) {
          if (rule.action.actionType === 'description') {
            rule.condition = excludeInvalidDescriptionRules(
              rule.condition,
              fieldIdToDelete
            );
          } else {
            rule.condition = excludeInvalidRules(
              rule.condition,
              fieldIdToDelete
            );
          }
          if (!JSON.parse(rule.condition).length) {
            if (!rules2DeleteFromState.includes(rule.id)) {
              rules2DeleteFromState.push(rule.id);
              fetchAPI(`/forms/v1/rules/${rule.id}`, { method: 'DELETE' });
            }
          } else {
            fetchAPI(`/forms/v1/rules/${rule.id}`, {
              method: 'PUT',
              body: rule
            });
          }
        }
      });

      let newRules = rules.filter(r => !rules2DeleteFromState.includes(r.id));

      setRules(newRules);
    },
    [rules, setRules, fetchAPI]
  );

  const setSelectedSubFieldsWrapper = a => {
    let order, orderedMatches;
    order = subFieldOptions.map(one => one.internalId);
    orderedMatches = order.filter(one => a.indexOf(one) !== -1);
    setSelectedSubFields(orderedMatches);
  };

  const setSelectedUserGroupsWrapper = a => {
    setSelectedUserGroups(a);
  };

  const filterEmptySections = (one, index, arr) => {
    if (one.uiType === 'section_break') {
      if (index === arr.length - 1) {
        return false;
      }
      if (arr[index + 1].uiType === 'section_break') {
        return false;
      }
    }
    return true;
  };

  const setSelectedFieldWrapper = f => {
    let s;

    if (selectedSubFields.length) {
      s = selectedSubFields;
      s = s.filter(one => one !== f);
      setSelectedSubFields(s);
    }
    setSubFieldOptions([
      ...extraOptions,
      ...unfilteredSubFieldOptions
        .filter(one => one.internalId !== f)
        .filter(filterEmptySections)
    ]);
    setSelectedField(f);
  };

  const removeDuplicates = arr => {
    let result = [],
      foundColumnHeaders = [],
      foundInternalIds = [];
    arr.forEach(one => {
      if (
        foundColumnHeaders.indexOf(one.columnHeader) === -1 &&
        foundInternalIds.indexOf(one.internalId) === -1
      ) {
        result.push(one);
        foundColumnHeaders.push(one.columnHeader);
        foundInternalIds.push(one.internalId);
      }
    });
    return result;
  };

  useEffect(() => {
    fetchAll(
      `/forms/v1/forms?query=[[{"field":"'archived'", "op": "IS NULL", "internOp": "OR"},{"field":"'archived'", "op": "=", "value": false, "internOp":"OR"}]]`
    ).then(res => {
      let results = [];
      if (res && res.results) {
        results = res.results;
      }
      results.sort((a, b) => {
        if (!a || !b || (!a.title && !b.title)) {
          return 0;
        }
        if (!a.title) {
          return -1;
        }
        if (!b.title) {
          return 1;
        }
        return a.title.localeCompare(b.title);
      });
      setForms(results);
    });
    if (props.lookupInfo) {
      if (props.entityType !== 'user' && props.lookupInfo.entityId) {
        setSelectedForm(props.lookupInfo.entityId);
      }
      if (props.entityType !== 'user' && props.lookupInfo.keyField) {
        setSelectedField(props.lookupInfo.keyField);
      }
      if (props.lookupInfo.subFields) {
        setSelectedSubFields(
          props.lookupInfo.subFields.map(one => one.internalId)
        );
      }
      if (props.entityType === 'user' && props.options) {
        setSelectedUserGroups(props.options);
      }
    }
  }, []);
  const previousSelectedForm = useRef();
  useEffect(() => {
    if (props.entityType === 'user' || (selectedForm && selectedField)) {
      setSaveDisabled(false);
    } else {
      setSaveDisabled(true);
    }
    if (
      selectedForm &&
      previousSelectedForm.current &&
      selectedForm !== previousSelectedForm.current
    ) {
      setSelectedField(null);
      setSelectedSubFields([]);
      setSaveDisabled(true);
    }
    if (selectedForm) {
      if (!previousSelectedForm.current) {
        setStep2Disabled(false);
      }
    } else {
      setStep2Disabled(true);
    }
    previousSelectedForm.current = selectedForm;
    if (props.entityType === 'user' || (selectedForm && selectedField)) {
      setStep3Disabled(false);
    } else {
      setStep3Disabled(true);
    }
  }, [props.entityType, selectedForm, selectedField, selectedSubFields]);
  useEffect(() => {
    if (props.entityType === 'user') {
      fetchAPI('/users/v1/usergroup').then(res => {
        if (res?.results && res.results.length) {
          setUserGroups(res.results);
        }
      });
      fetchAPI('/users/v1/userschema').then(res => {
        let schemas, schemaFields;
        schemaFields = [];
        if (res && res.results && res.results.length) {
          schemas = res.results.filter(one => one.code === 'internal') || [];
          if (schemas && schemas.length) {
            schemaFields = schemas[0].fields.filter(one => one.ignore !== true);
          }
        }
        schemaFields.map(one => {
          one.title = one.columnHeader;
          return one;
        });
        schemaFields = removeDuplicates(schemaFields);
        setFields(schemaFields);
        setSubFieldOptions(schemaFields);
      });
    } else if (selectedForm) {
      setStep2Disabled(true);
      fetchAPI(`/forms/v1/forms/${selectedForm}`).then(res => {
        let results = [],
          newUnfilteredSubFieldOptions = [];
        if (res && res.fields) {
          let fieldsToFilter = [
            ...res.fields,
            ...(props.isAdminOnly ? res.adminFields : [])
          ];
          results = fieldsToFilter.filter(one => {
            if (
              !one.title ||
              one.archived ||
              [
                'section_break',
                'text',
                'long_text',
                'number',
                'currency',
                'dropdown',
                'email_address',
                'yes_no',
                'date',
                'multiple_choice',
                'link'
              ].indexOf(one.uiType) === -1
            ) {
              return false;
            }
            return true;
          });
          results.sort((a, b) => a.position - b.position);
          newUnfilteredSubFieldOptions = results.slice();
        }
        setUnfilteredSubFieldOptions(newUnfilteredSubFieldOptions);
        setSubFieldOptions([
          ...extraOptions,
          ...newUnfilteredSubFieldOptions
            .filter(one => !selectedField || one.internalId !== selectedField)
            .filter(filterEmptySections)
        ]);
        setFields(results);
        setFieldOptions(
          results
            .filter(
              one => ['yes_no', 'multiple_choice'].indexOf(one.uiType) === -1
            )
            .filter(filterEmptySections)
        );
        if (res.customStatuses) {
          setCustomStatuses(res.customStatuses.map(one => one.internalId));
        }
        setStep2Disabled(false);
      });
    }
  }, [selectedForm]);
  const viewProps = {
    clickedSave,
    fieldOptions,
    forms,
    saveDisabled,
    selectedSubFields,
    setSelectedSubFields: setSelectedSubFieldsWrapper,
    selectedUserGroups,
    setSelectedUserGroups: setSelectedUserGroupsWrapper,
    selectedField,
    setSelectedField: setSelectedFieldWrapper,
    selectedForm,
    setSelectedForm,
    step2Disabled,
    step3Disabled,
    subFieldOptions,
    userGroups,
    ...props
  };
  return <LookupSettingsPanelView {...viewProps} />;
};

export default LookupSettingsPanelEngine;
